import type { HawkJavaScriptEvent } from "@hawk.so/browser"
import HawkCatcher from "@hawk.so/browser"
import type { EventContext } from "@hawk.so/types"
import { isVoximplantNoiseError } from "shared/lib/voximplant/is-voximplant-noise-error"
import { installVoximplantRejectionGuard } from "shared/lib/voximplant/safe-init-vox-kit-widget"

import { isCorsError } from "@app/lib/hawk/is-cors-error"
import type { GetSessionQueryResult } from "@entities/user/models"

type HawkSession = GetSessionQueryResult["result"]["result"]["session"]

let hawk: HawkCatcher | null = null

const shouldDropHawkEvent = (event: HawkJavaScriptEvent): boolean => {
  const title = event.title ?? ""
  const frames = event.backtrace ?? []
  const fromVoximplant = frames.some((frame) => String(frame.file ?? "").includes("kit.voximplant.com"))

  if (isCorsError(title)) {
    return true
  }

  if (
    isVoximplantNoiseError({ name: "InvalidStateError", message: title }) ||
    (fromVoximplant && title.includes("Transition was aborted"))
  ) {
    return true
  }

  return false
}

export function initHawk() {
  if (typeof window === "undefined") {
    return null
  }

  // INFO [a.savrov (31-07-2026 - 20:06)]: до HawkCatcher, чтобы stopImmediatePropagation отсёк шум voximplant
  installVoximplantRejectionGuard()

  const token = process.env.NEXT_PUBLIC_HAWK_TOKEN

  if (!token) {
    return null
  }

  if (!hawk) {
    try {
      hawk = new HawkCatcher({
        token,
        release: process.env.NEXT_PUBLIC_RELEASE,
        beforeSend(event) {
          if (shouldDropHawkEvent(event)) {
            return false
          }

          return event
        },
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- hawk SDK extends window
      ;(window as any).hawk = hawk
    } catch (error) {
      console.error("Ошибка инициализации Hawk:", error)
    }
  }

  return hawk
}

export function getHawk() {
  return hawk
}

/** Актуальная сессия в context.session для всех последующих событий Hawk. */
export function setHawkSessionContext(session: HawkSession | null | undefined) {
  const hawkInstance = getHawk()
  if (!hawkInstance || !session) return

  hawkInstance.setContext({ session } as unknown as EventContext)
}

export function testHawk() {
  const hawkInstance = getHawk()
  if (hawkInstance) {
    hawkInstance.test()
  }
}
